import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, Mail, Phone, Trash2, Send, StickyNote, ExternalLink } from "lucide-react";
import { CaseManagerSelect } from "./CaseManagerSelect";
import { STATUSES, statusStyle } from "./admin/leadStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CH_COMPANY_URL = "https://find-and-update.company-information.service.gov.uk/company";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const LeadDrawer = ({ lead, onClose, onStatusChange, onRefresh, caseManagers, onCaseManagerChange, onCaseManagersOptionsChange, viewerRole = "admin" }) => {
  const isAdmin = viewerRole === "admin";
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${lead.id}/notes`, { text }, { withCredentials: true });
      setText("");
      await onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    await axios.delete(`${API}/leads/${lead.id}/notes/${noteId}`, { withCredentials: true });
    await onRefresh();
  };

  const details = [
    ["Company number", lead.company_number],
    ["Registered address", lead.company_address],
    ["Loan amount", lead.loan_amount],
    ["Loan purpose", lead.loan_purpose],
    ["Annual turnover", lead.annual_turnover],
    ["Monthly sales", lead.monthly_sales],
    ["Time trading", lead.trading_time],
    ["Homeowner", lead.homeowner],
    ["Referred by", lead.referred_by],
    ["Packager", lead.packager],
    ["Received", fmtDate(lead.created_at)],
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-[#052F24]/40 backdrop-blur-sm"
        data-testid="drawer-overlay"
      />
      <motion.aside
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
        className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
        data-testid="lead-drawer"
      >
        <div className="flex items-start justify-between p-6 border-b border-[#E5E7EB]">
          <div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight" data-testid="drawer-name">{lead.full_name}</h2>
            <p className="text-sm text-[#6B7280]">{lead.business_name}</p>
          </div>
          <button onClick={onClose} data-testid="drawer-close-btn" className="p-2 rounded-full hover:bg-[#F3F4F6] transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={lead.status}
              onChange={(e) => onStatusChange(lead.id, e.target.value)}
              data-testid="drawer-status-select"
              className={`rounded-full px-3 py-1.5 text-xs font-bold border-0 outline-none cursor-pointer ${statusStyle[lead.status] || statusStyle.New}`}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <CaseManagerSelect
              value={lead.case_manager}
              options={caseManagers || []}
              onChange={(name) => onCaseManagerChange(lead.id, name)}
              onOptionsChange={onCaseManagersOptionsChange}
              testId="drawer-case-manager-select"
            />
            <a href={`mailto:${lead.email}`} data-testid="drawer-email-link" className="flex items-center gap-1.5 rounded-full bg-[#F3F4F6] px-3 py-1.5 text-xs font-semibold text-[#052F24] hover:bg-[#E5E7EB] transition-colors">
              <Mail size={12} /> {lead.email}
            </a>
            <a href={`tel:${lead.phone}`} data-testid="drawer-phone-link" className="flex items-center gap-1.5 rounded-full bg-[#F3F4F6] px-3 py-1.5 text-xs font-semibold text-[#052F24] hover:bg-[#E5E7EB] transition-colors">
              <Phone size={12} /> {lead.phone}
            </a>
            {lead.company_number && (
              <a
                href={`${CH_COMPANY_URL}/${lead.company_number}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="drawer-companies-house-link"
                className="flex items-center gap-1.5 rounded-full bg-[#DFFF00]/60 px-3 py-1.5 text-xs font-semibold text-[#052F24] hover:bg-[#DFFF00] transition-colors"
              >
                <ExternalLink size={12} /> View on Companies House
              </a>
            )}
          </div>

          <div className="rounded-2xl border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
            {details.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 px-4 py-3 text-sm">
                <span className="text-[#6B7280]">{k}</span>
                <span className="font-semibold text-[#111827] text-right">{v || "—"}</span>
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <StickyNote size={16} className="text-[#052F24]/50" />
              <h3 className="font-display font-bold text-sm tracking-wide uppercase text-[#052F24]/70">Notes & follow-ups</h3>
            </div>
            {(!lead.notes || lead.notes.length === 0) ? (
              <p className="text-sm text-[#9CA3AF] mb-3" data-testid="notes-empty">No notes yet. Add your first call note below.</p>
            ) : (
              <div className="space-y-2 mb-3" data-testid="notes-list">
                {[...lead.notes].reverse().map((n) => (
                  <div key={n.id} className="group rounded-xl bg-[#F9FAFB] border border-[#E5E7EB] p-3" data-testid={`note-${n.id}`}>
                    <p className="text-sm text-[#111827] whitespace-pre-wrap">{n.text}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] text-[#9CA3AF]">{fmtDate(n.created_at)}</span>
                      <button onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`} className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-red-500 transition-all">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. Called at 2pm — wants £50k over 12 months, follow up Friday"
                rows={2}
                data-testid="note-input"
                className="flex-1 rounded-xl border border-[#E5E7EB] p-3 text-sm outline-none focus:border-[#052F24]/40 resize-none"
              />
              <button
                onClick={addNote}
                disabled={saving || !text.trim()}
                data-testid="add-note-btn"
                className="self-end rounded-full bg-[#052F24] text-[#DFFF00] p-3 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  );
};
